/**
 * Client-error telemetry — first-party crash/error reporting (NFR-F-OBS-001).
 *
 * Posts caught render crashes (error boundaries) and uncaught window
 * errors/rejections to a first-party backend endpoint so UAT/prod crashes
 * leave a trace in the in-house log stream instead of being discarded or
 * trapped in an ephemeral browser console.
 *
 * Deliberately NOT routed through apiClient — every choice here is load-bearing:
 *   - It must work when the app is already broken (a render crash), so it uses
 *     a bare ``fetch`` with no store/React dependencies in the hot path.
 *   - ``keepalive: true`` lets the request complete across the crash, a
 *     navigation, or a hard reload.
 *   - It must NOT recurse: apiClient errors must not themselves be reported
 *     through a path that can fail and re-report.
 *   - It must NOT be subject to apiClient's 401→logout (NFR-F-SEC-003). A
 *     telemetry auth blip must never log the tester out mid-assessment.
 *   - It must NEVER throw — every failure (including its own) is swallowed.
 *
 * PII-safe: only message / stack / componentStack / route / release / UA are
 * sent in the body — never form state, candidate answers, or tokens. The
 * bearer token is attached as a header (not the body) when present so the
 * backend can derive the caller's role/user_id from the JWT for triage.
 */

import { apiUrl } from '../config/api';
import { useAuthStore } from '../stores/authStore';

export type ClientErrorKind = 'render' | 'route' | 'window.onerror' | 'unhandledrejection';

const ENDPOINT = '/api/telemetry/client-error';

/** Minimal shape of React's ErrorInfo — we only need the component stack. */
export interface ClientErrorInfo {
  componentStack?: string | null;
}

/** Build-time release id (commit sha / tag) if the bundler injected one. */
function releaseId(): string {
  try {
    return (import.meta.env?.VITE_RELEASE as string | undefined) ?? '';
  } catch {
    return '';
  }
}

/**
 * Report a single client-side error to the first-party telemetry endpoint.
 * Fire-and-forget; never throws and never rejects to the caller.
 *
 * @param error  the thrown value (Error, string, or anything)
 * @param info   React ErrorInfo (component stack) when reporting from a boundary
 * @param kind   where the error originated
 */
export function reportClientError(
  error: unknown,
  info: ClientErrorInfo | undefined,
  kind: ClientErrorKind,
): void {
  try {
    const err = error as { message?: unknown; stack?: unknown } | null | undefined;
    const message =
      (err && typeof err.message === 'string' && err.message) ||
      (typeof error === 'string' ? error : '') ||
      'Unknown error';
    const stack = err && typeof err.stack === 'string' ? err.stack : undefined;
    const componentStack = info?.componentStack ?? undefined;

    // Read the bearer token off the store (header only — never in the body).
    // Wrapped because the store could be mid-tear-down during a crash.
    let token: string | null = null;
    try {
      token = useAuthStore.getState().token ?? null;
    } catch {
      /* store unavailable — report anonymously */
    }

    const body = JSON.stringify({
      message,
      stack,
      componentStack,
      kind,
      route: typeof window !== 'undefined' ? window.location?.pathname : undefined,
      releaseId: releaseId(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      ts: new Date().toISOString(),
    });

    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    // Raw fetch — NOT apiClient (see file header). keepalive survives a
    // crash/navigation/reload; we never await and swallow any rejection so
    // telemetry can't itself throw or surface an error to a crashing client.
    void fetch(apiUrl(ENDPOINT), {
      method: 'POST',
      keepalive: true,
      headers,
      body,
    }).catch(() => {
      /* telemetry must never throw */
    });
  } catch {
    /* telemetry must never throw — swallow everything, including sync errors */
  }
}

let handlersRegistered = false;

/**
 * Register global handlers for uncaught errors + unhandled promise rejections,
 * exactly once. Called at bootstrap (main.tsx). Idempotent so repeated calls
 * (HMR, StrictMode double-invoke, accidental re-import) don't stack listeners.
 */
export function registerGlobalErrorHandlers(): void {
  if (handlersRegistered || typeof window === 'undefined') return;
  handlersRegistered = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    // event.error is the thrown value when available; fall back to the message
    // (e.g. cross-origin script errors expose only a string).
    reportClientError(event.error ?? event.message, undefined, 'window.onerror');
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    reportClientError(event.reason, undefined, 'unhandledrejection');
  });
}
